/**
 * Shared expiry-window logic for the dashboard "Expiry Overview" buttons
 * and the customer list pages they link to. Mirrors
 * frontend_api/dashboard/expiry_helpers.php so the number on a dashboard
 * button always matches the rows you see after clicking it.
 */

// [fromOffset, toOffset] in days relative to today, inclusive.
export const EXPIRY_BUCKETS = [
  { key: "last_7_days", label: "Last 7 days", short: "Expired, last 7 days", range: [-7, -1], tone: "past" },
  { key: "yesterday", label: "Yesterday", short: "Expired yesterday", range: [-1, -1], tone: "past" },
  { key: "today", label: "Today", short: "Expires today", range: [0, 0], tone: "today" },
  { key: "tomorrow", label: "Tomorrow", short: "Expires tomorrow", range: [1, 1], tone: "soon" },
  { key: "next_7_days", label: "Next 7 days", short: "Expires in next 7 days", range: [1, 7], tone: "soon" },
];

export const getExpiryBucket = (key) => EXPIRY_BUCKETS.find((b) => b.key === key) || null;

const pad = (n) => String(n).padStart(2, "0");
// Fixed names: the browser's en-IN locale writes "Sept", we want "Sep".
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Today's date as YYYY-MM-DD in the browser's local time. */
export const todayYmd = () => toYmd(new Date());

const addDays = (ymd, days) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return toYmd(new Date(y, m - 1, d + days));
};

const isValidYmd = (y, m, d) => {
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

/**
 * Normalises any provider date to YYYY-MM-DD (or null).
 * Accepts "2026-10-01", "2026-10-01 23:59:59", "01-10-2026", "01/10/2026".
 */
export const normalizeExpiry = (value) => {
  if (value === null || value === undefined) return null;
  const v = String(value).trim();
  if (!v || v.startsWith("0000-00-00")) return null;

  let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return isValidYmd(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }
  m = v.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (m) {
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return isValidYmd(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }
  // OneRADIUS (Pioneer / DVPL): "25 Aug, 2026 12:09 am", "25 Aug 2026", "25-Aug-2026"
  m = v.match(/^(\d{1,2})[\s\-/]+([A-Za-z]{3,9})[\s,\-/]+(\d{4})/);
  if (m) {
    const mo = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"]
      .indexOf(m[2].slice(0, 3).toLowerCase()) + 1;
    const [d, y] = [Number(m[1]), Number(m[3])];
    return mo && isValidYmd(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }
  const parsed = new Date(v);
  return Number.isNaN(parsed.getTime()) ? null : toYmd(parsed);
};

/** Does a raw expiry value fall in the given bucket? */
export const isInExpiryBucket = (value, bucketKey, today = todayYmd()) => {
  const bucket = getExpiryBucket(bucketKey);
  const date = normalizeExpiry(value);
  if (!bucket || !date) return false;
  const from = addDays(today, bucket.range[0]);
  const to = addDays(today, bucket.range[1]);
  return date >= from && date <= to;
};

/** Active = expiry date is today or later (expires at end of day). */
export const isExpiryActive = (value, today = todayYmd()) => {
  const date = normalizeExpiry(value);
  return !!date && date >= today;
};

/** Days from today (negative = already expired), or null. */
export const daysUntil = (value, today = todayYmd()) => {
  const date = normalizeExpiry(value);
  if (!date) return null;
  const [y1, m1, d1] = today.split("-").map(Number);
  const [y2, m2, d2] = date.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};

/** Human hint like "in 3 days", "today", "2 days ago". */
export const relativeExpiryText = (value) => {
  const n = daysUntil(value);
  if (n === null) return "";
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
};

/** Formats YYYY-MM-DD as "01 Oct 2026" for display. */
export const formatExpiryDate = (value) => {
  const date = normalizeExpiry(value);
  if (!date) return "N/A";
  const [y, m, d] = date.split("-").map(Number);
  return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
};

/** Indian digit grouping (1,23,456). Safe for undefined/null/strings. */
export const formatCount = (num) => {
  const n = Number(num);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
};

/**
 * Full date-time when the provider gives one (Pioneer/DVPL send
 * "25 Aug, 2026 12:09 am", Pioneer IPTV "2026-08-25 00:09:00").
 * Returns { date: Date, hasTime } or null.
 */
export const parseExpiryDateTime = (value) => {
  const ymd = normalizeExpiry(value);
  if (!ymd) return null;
  const [y, mo, d] = ymd.split("-").map(Number);
  const v = String(value);
  let m = v.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?/i);
  if (!m || /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return { date: new Date(y, mo - 1, d), hasTime: false };
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (m[3]) {
    const pm = m[3].toLowerCase() === "pm";
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
  }
  // "00:00:00" on a date-only DB column isn't a real time — treat as date only.
  if (h === 0 && min === 0 && !m[3]) return { date: new Date(y, mo - 1, d), hasTime: false };
  return { date: new Date(y, mo - 1, d, h, min), hasTime: true };
};

/** "01 Oct 2026" or "01 Oct 2026, 10:37 am" when a time is known. */
export const formatExpiryDateTime = (value) => {
  const p = parseExpiryDateTime(value);
  if (!p) return "N/A";
  const datePart = `${pad(p.date.getDate())} ${MONTHS[p.date.getMonth()]} ${p.date.getFullYear()}`;
  if (!p.hasTime) return datePart;
  const timePart = p.date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${datePart}, ${timePart}`;
};

/** Badge for the expiry: { text, tone: "expired" | "today" | "active" } or null. */
export const expiryStatus = (value) => {
  const n = daysUntil(value);
  if (n === null) return null;
  if (n === 0) return { text: "Expires Today", tone: "today" };
  if (n > 0) return { text: `${n} ${n === 1 ? "Day" : "Days"} Left`, tone: "active" };
  return { text: `Expired ${-n} ${n === -1 ? "day" : "days"} ago`, tone: "expired" };
};
